import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { sanitize } from 'dompurify'
import { Grid, Typography, Divider, Alert, Box, Link, IconButton, Button, Menu, MenuItem } from '@mui/material'
import FavoriteIcon from '@mui/icons-material/Favorite'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import AddIcon from '@mui/icons-material/Add'
import UploadIcon from '@mui/icons-material/Upload'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import ListDataItem from 'components/Listing/ListDataItem'
import { getImage, getJobListItems, getJobUrl } from './helpers'
import RecruiterCard from './RecruiterCard'
import { convertNewlinesToBreaks } from './helpers'
import { useJobs } from './useJobs'
import ApplicationDrawer from './ApplicationDrawer'
import JobActionsButton from './JobActionsButton'
import JobDrawer from 'components/Drawers/JobDrawer/JobDrawer'
import { fetchArchiveJob } from 'src/redux/jobs'
import { DeleteDialogButton } from 'components/Dialog/DialogButton'
import { format } from 'date-fns' // Importing date formatting utility

// Date formatting function for the posted date
const formatPostedDate = date => format(new Date(date), 'MMMM dd, yyyy')

const generateJobPostingSchema = job => {
  // Helper function to strip HTML tags and get plain text
  const getPlainTextDescription = (htmlContent) => {
    if (!htmlContent) return ''
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = htmlContent
    return tempDiv.textContent || tempDiv.innerText || ''
  }

  let employmentType
  switch (job.practiceType) {
    case 'Telemedicine':
      employmentType = 'FULL_TIME'
      break
    case 'FullTime':
      employmentType = 'FULL_TIME'
      break
    case 'PRN':
      employmentType = 'PER_DIEM'
      break
    case 'Locum':
      employmentType = 'TEMPORARY'
      break
    default:
      employmentType = 'PART_TIME'
      break
  }

  // Get plain text description
  let description = ''
  if (job.about || job.aboutRaw) {
    description = getPlainTextDescription(job.about || job.aboutRaw)
  }

  // Fallback description if empty
  if (!description || description.trim().length === 0) {
    description = `${job.title} position at ${job.employerName} in ${job.city}, ${job.state}. Apply now to learn more about this healthcare opportunity.`
  }

  // Build the schema object
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: job.title,
    description: description,
    datePosted: new Date(job.createdAt).toISOString(),
    employmentType: employmentType,
    hiringOrganization: {
      '@type': 'Organization',
      name: job.employerName,
      sameAs: job.link || 'https://www.mystethi.com',
    },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressLocality: job.city,
        addressRegion: job.state,
        addressCountry: 'US',
      },
    },
    qualifications: job.specialty,
    validThrough: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
  }

  // Add salary data if available and realistic (> 1000)
  if (job.salaryMin && job.salaryMax && job.salaryMin > 1000 && job.salaryMax > 1000) {
    schema.baseSalary = {
      '@type': 'MonetaryAmount',
      currency: 'USD',
      value: {
        '@type': 'QuantitativeValue',
        minValue: job.salaryMin,
        maxValue: job.salaryMax,
        unitText: job.salaryType === 'Hourly' ? 'HOUR' : 'YEAR'
      }
    }
  } else if (job.salary && job.salary > 1000) {
    schema.baseSalary = {
      '@type': 'MonetaryAmount',
      currency: 'USD',
      value: {
        '@type': 'QuantitativeValue',
        value: job.salary,
        unitText: job.salaryType === 'Hourly' ? 'HOUR' : 'YEAR'
      }
    }
  }

  return schema
}

const JobContent = ({
  item = {},
  signedIn,
  employerUserId,
  onFavoriteClick,
  jobs,
  activeItem,
  mobile = false,
  onUploadMenuClick,
  uploadMenuAnchor,
  onUploadMenuClose,
  onManualUpload,
}) => {
  if (!item) return null

  useEffect(() => {
    document.title = `MyStethi - ${item?.title ? `${item.title} - ` : ''}${item?.employerName || 'Jobs'}`
  }, [item?.title, item?.employerName])

  const fields = getJobListItems(item, signedIn) || []
  const displayableFields = fields.filter(field => field.show)
  const dispatch = useDispatch()
  const employerImage = getImage(item?.employer?.image)
  const candidateRating = item.candidateRating ? item.candidateRating : {}
  const deletedAt = 'Archived: ' + new Date(item.deletedAt).toDateString()
  const archive = item.deletedAt === null
  const about = item.about !== '' && item.about !== null ? item.about : item.aboutRaw
  const aboutContent = convertNewlinesToBreaks(about)
  const {
    drawerJobApplicationOpen,
    handleJobApplicationDrawerClose,
    setJobApplicationDrawerOpen,
    drawerJobOpen,
    setDrawerJobOpen,
  } = useJobs()
  const [isEdit, setEdit] = useState(null)

  const handleEdit = () => {
    setEdit(true)
    setDrawerJobOpen(true)
  }

  return (
    <>
      <JobDrawer
        open={drawerJobOpen}
        onClose={() => setDrawerJobOpen(false)}
        jobToEdit={item}
        isEdit={isEdit}
        setOpenDrawer={setDrawerJobOpen}
      />
      {activeItem ? (
        <Box display={{ xs: 'block', sm: 'block', md: 'contents' }}>
          <script type='application/ld+json'>
            {JSON.stringify(generateJobPostingSchema(item))}
          </script>
          <Grid container sx={{ padding: '20px 20px', height: 'auto', marginBottom: '100px' }}>
            <Grid container item spacing={1} alignItems='center' direction='row' xs={12}>
              <Grid container item xs={9} alignItems='center'>
                <Typography variant='h4' sx={{ display: 'flex', alignItems: 'center' }}>
                  <Link
                    href={getJobUrl(item)}
                    target='_blank'
                    sx={{
                      color: 'inherit',
                      textDecoration: 'none !important',
                      background: 'none !important',
                      backgroundSize: '0 !important',
                      display: 'inline',
                      '&:hover': {
                        textDecoration: 'none !important',
                        cursor: 'pointer',
                        background: 'none !important',
                        backgroundSize: '0 !important',
                      },
                      '&:focus': {
                        textDecoration: 'none !important',
                        background: 'none !important',
                        backgroundSize: '0 !important',
                      },
                    }}
                  >
                    <b>{item.title} <OpenInNewIcon sx={{ fontSize: '0.8rem', opacity: 0.7, verticalAlign: 'super', marginLeft: '2px' }} /></b>
                  </Link>
                  {signedIn && !employerUserId && (
                    <IconButton
                      aria-label='favorite'
                      onClick={() =>
                        onFavoriteClick({
                          jobId: item.id,
                          favorite: item.candidateJob ? !item.candidateJob.favorite : true,
                        })
                      }
                      sx={{ marginLeft: 2 }}
                    >
                      {item.candidateJob && item.candidateJob.favorite ? (
                        <FavoriteIcon color='error' />
                      ) : (
                        <FavoriteBorderIcon color='error' />
                      )}
                    </IconButton>
                  )}
                </Typography>
              </Grid>

              {/* Upload jobs button */}
              {signedIn && employerUserId && (
                <Grid container item xs={3} justifyContent='flex-end'>
                    <Button
                      variant='contained'
                      size='small'
                      startIcon={<AddIcon />}
                      onClick={onUploadMenuClick}
                      sx={{ textTransform: 'none' }}
                    >
                      Upload Jobs
                    </Button>
                </Grid>
              )}
            </Grid>

            {/* Employer Name, Posted Date, Archived Alert, and Editable Job Buttons */}
            <Grid container item xs={12} direction='row' alignItems='flex-start' spacing={1}>
              {/* Employer Name */}
              <Grid item xs={12}>
                <Typography variant='h6'>
                  <b>{item.employerName}</b>
                </Typography>
              </Grid>

              {/* Posted Date */}
              {item.createdAt && (
                <Grid item xs={12}>
                  <Typography variant='caption' color='textSecondary'>
                    Posted on: {formatPostedDate(item.createdAt)}
                  </Typography>
                </Grid>
              )}

              {/* Archived Tag */}
              {item.deletedAt !== null && (
                <Grid item xs={12} sx={{ marginBottom: '10px' }}>
                  <Alert icon={false} severity='info'>
                    {deletedAt}
                  </Alert>
                </Grid>
              )}
              {signedIn && employerUserId && (
                <Grid container item xs={6} flexDirection='row' sx={{ marginTop: '2px' }}>
                  <Button variant='outlined' size='small' onClick={() => handleEdit()}>
                    Edit
                  </Button>
                  <Grid item sx={{ paddingLeft: '5px' }}>
                    <DeleteDialogButton
                      size='small'
                      isDeleted={item.deletedAt !== null}
                      onClick={() =>
                        dispatch(
                          fetchArchiveJob({
                            id: item.id,
                            archive: archive,
                            employerId: item.employer.id,
                          }),
                        )
                      }
                    >
                      {archive ? 'Archive Job' : 'Restore Job'}
                    </DeleteDialogButton>
                  </Grid>
                </Grid>
              )}
              {signedIn && !employerUserId && (
                <Grid container item xs={12} spacing={2} flexDirection='row'>
                  <JobActionsButton
                    jobId={item.id}
                    candidateRating={candidateRating}
                    candidateJobApplication={item.candidateJobApplication}
                    handleDrawerOpen={setJobApplicationDrawerOpen}
                    mobile={mobile}
                  />
                </Grid>
              )}
            </Grid>

            <Grid item xs={12} sx={{ margin: '10px 0px' }}>
              <Divider sx={{ height: '1px', marginBottom: '10px' }} />
            </Grid>

            {/* Job Content */}
            <Grid container item xs={12} direction='row'>
              <Grid container item sm={12}>
                <RecruiterCard
                  recruiter={item.employerUser}
                  jobId={item.id}
                  signedIn={signedIn}
                  employerUserId={employerUserId}
                />
              </Grid>
              <Grid
                container
                item
                justifyContent='space-between'
                sm={12}
                md={12}
                rowSpacing={1}
                sx={{ marginBottom: '10px' }}
              >
                {displayableFields.map((field, index) => (
                  <Grid item xs={6} key={index}>
                    <ListDataItem
                      icon={field.icon}
                      boolValue={field.value}
                      text={field.text}
                      isLink={field.isLink}
                      link={field.link}
                    />
                  </Grid>
                ))}
              </Grid>
              {employerImage && (
                <Grid container item xs={12}>
                  <Box
                    component='img'
                    sx={{
                      height: '100px',
                      width: '100%',
                      overflow: 'hidden',
                      objectFit: 'cover',
                      margin: '20px 0px',
                    }}
                    alt={item.employerName}
                    src={employerImage}
                  />
                </Grid>
              )}
              {about && (
                <Grid
                  item
                  xs={12}
                  justifyContent='flex-start'
                  sx={{ marginTop: '10px', marginBottom: '50px' }}
                >
                  <Divider sx={{ height: '1px', margin: '20px 0px' }} />
                  <Typography
                    variant='body1'
                    dangerouslySetInnerHTML={{ __html: sanitize(aboutContent) }}
                  />
                </Grid>
              )}
              {!about && item.link && !employerUserId && (
                <Typography variant='body1' sx={{ marginTop: '10px' }}>
                  For more information about this job, please visit{' '}
                  <Link href={item.link} target='_blank'>
                    {item.employerName}'s website
                  </Link>
                </Typography>
              )}
              {item.employer.about && (
                <Grid
                  item
                  xs={12}
                  justifyContent='flex-start'
                  sx={{ marginTop: '10px', marginBottom: '50px' }}
                >
                  <Divider sx={{ height: '1px' }} />
                  <Typography variant='h4'>About {item.employerName}</Typography>
                  <Grid container>
                    <Grid item xs={12}>
                      <Typography
                        variant='body1'
                        dangerouslySetInnerHTML={{ __html: sanitize(item.employer.about) }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>

          {signedIn && !employerUserId && (
            <ApplicationDrawer
              open={drawerJobApplicationOpen}
              onClose={handleJobApplicationDrawerClose}
              jobs={jobs}
              job={item}
            />
          )}
        </Box>
      ) : employerUserId ? (
        <Box sx={{ padding: '50px', textAlign: 'center' }}>
          <Typography variant='h6' gutterBottom color='text.secondary'>
            No jobs posted yet
          </Typography>
          <Typography variant='body2' sx={{ mb: 3, color: 'text.secondary' }}>
            Get started by uploading your job postings
          </Typography>
          <Button
            variant='contained'
            size='large'
            startIcon={<AddIcon />}
            onClick={onUploadMenuClick}
            sx={{ textTransform: 'none' }}
          >
            Upload Jobs
          </Button>
        </Box>
      ) : (
        <Typography variant='body2' sx={{ padding: '50px' }}>
          No jobs found, please try a different filter criteria
        </Typography>
      )}

      {/* Upload method selection menu */}
      {signedIn && employerUserId && (
        <Menu
          anchorEl={uploadMenuAnchor}
          open={Boolean(uploadMenuAnchor)}
          onClose={onUploadMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <MenuItem onClick={onManualUpload}>
            <UploadIcon sx={{ mr: 2 }} />
            Manual Upload
          </MenuItem>
        </Menu>
      )}
    </>
  )
}

export default JobContent
